import type { CivilizationResources } from '@galaxis/shared';

/**
 * Client-side preview of the stockpile between two server snapshots. The server stays authoritative
 * (it settles production on every read and never trusts this number): the display only ticks forward
 * `amount + rate × elapsed`, clamped to the storage capacity, and snaps to the next snapshot.
 */

/** The snapshot the display is anchored to. */
export interface ResourceBase {
  /** Values as of `anchorMs` (the server snapshot, or a snapshot minus a spend preview). */
  res: CivilizationResources;
  /** Server time (ms) the values refer to. */
  anchorMs: number;
  /** Server time (ms) of the last authoritative snapshot: older snapshots are ignored. */
  serverMs: number;
  /** serverClock − clientClock (ms), measured when the snapshot arrived. */
  offsetMs: number;
}

export interface LiveResources {
  metal: number;
  energy: number;
  credits: number;
  /** Research points; undefined when the server sent none. */
  rp?: number;
  rpPerHour?: number;
  metalPerHour: number;
  energyPerHour: number;
  metalCapacity: number | null;
  energyCapacity: number | null;
  metalUpkeepPerHour?: number;
  energyUpkeepPerHour?: number;
  /** Mining efficiency (1 = full). */
  efficiency: number;
  /** Stockpile is at capacity and still producing: the counter stands still. */
  metalFull: boolean;
  energyFull: boolean;
  /** Net rate is negative and the stockpile is gone (the counter stands at 0). */
  metalEmpty: boolean;
  energyEmpty: boolean;
  /** Game hours (real hours) until the stockpile is full / empty at the current rate; null if it never will be. */
  metalFullInH: number | null;
  energyFullInH: number | null;
  metalEmptyInH: number | null;
  energyEmptyInH: number | null;
  /** Energy shortage: the stock is draining or mining runs at reduced efficiency. */
  deficit: boolean;
}

export function parseServerMs(res: CivilizationResources, fallbackMs: number): number {
  const t = res.serverTime ? Date.parse(res.serverTime) : NaN;
  return Number.isFinite(t) ? t : fallbackMs;
}

/**
 * Anchors a fresh snapshot. `receivedAt` is the client clock when the response arrived and `rttMs` the request's
 * round trip: the server stamped the values roughly half a round trip before they arrived.
 */
export function makeBase(res: CivilizationResources, receivedAt: number, rttMs = 0): ResourceBase {
  const serverMs = parseServerMs(res, receivedAt - rttMs / 2);
  return { res, anchorMs: serverMs, serverMs, offsetMs: serverMs - (receivedAt - rttMs / 2) };
}

/** True when `res` is not older than the snapshot already held (responses may arrive out of order). */
export function isFresher(prev: ResourceBase | null, res: CivilizationResources): boolean {
  if (!prev || !res.serverTime) return true;
  const t = Date.parse(res.serverTime);
  return !Number.isFinite(t) || t >= prev.serverMs;
}

/**
 * One stock after `hours`: growth stops at the capacity (a larger stock stays as it is), a negative rate stops at 0.
 * Unrounded; display code floors.
 */
export function projectStock(amount: number, ratePerHour: number, hours: number, capacity: number | null): number {
  if (hours <= 0 || ratePerHour === 0) return amount;
  const next = amount + ratePerHour * hours;
  if (ratePerHour > 0) return capacity === null ? next : Math.min(next, Math.max(amount, capacity));
  return Math.max(0, next);
}

function untilHours(amount: number, rate: number, target: number): number | null {
  if (rate === 0) return null;
  const h = (target - amount) / rate;
  return h > 0 && Number.isFinite(h) ? h : null;
}

export function projectResources(base: ResourceBase, clientNow: number): LiveResources {
  const { res } = base;
  const serverNow = clientNow + base.offsetMs;
  const hours = Math.max(0, (serverNow - base.anchorMs) / 3_600_000);
  const metalCap = res.metalCapacity ?? null;
  const energyCap = res.energyCapacity ?? null;
  const metalRaw = projectStock(res.metal, res.metalPerHour, hours, metalCap);
  const energyRaw = projectStock(res.energy, res.energyPerHour, hours, energyCap);
  const metal = Math.floor(metalRaw);
  const energy = Math.floor(energyRaw);
  const efficiency = res.efficiency ?? 1;
  const rp = res.rp === undefined ? undefined : Math.floor(projectStock(res.rp, res.rpPerHour ?? 0, hours, null));
  return {
    metal,
    energy,
    credits: res.credits,
    rp,
    rpPerHour: res.rpPerHour,
    metalPerHour: res.metalPerHour,
    energyPerHour: res.energyPerHour,
    metalCapacity: metalCap,
    energyCapacity: energyCap,
    metalUpkeepPerHour: res.metalUpkeepPerHour,
    energyUpkeepPerHour: res.energyUpkeepPerHour,
    efficiency,
    metalFull: metalCap !== null && res.metalPerHour > 0 && metal >= metalCap,
    energyFull: energyCap !== null && res.energyPerHour > 0 && energy >= energyCap,
    metalEmpty: res.metalPerHour < 0 && metal <= 0,
    energyEmpty: res.energyPerHour < 0 && energy <= 0,
    metalFullInH: metalCap !== null && res.metalPerHour > 0 && metal < metalCap ? untilHours(metalRaw, res.metalPerHour, metalCap) : null,
    energyFullInH: energyCap !== null && res.energyPerHour > 0 && energy < energyCap ? untilHours(energyRaw, res.energyPerHour, energyCap) : null,
    metalEmptyInH: res.metalPerHour < 0 && metal > 0 ? untilHours(metalRaw, res.metalPerHour, 0) : null,
    energyEmptyInH: res.energyPerHour < 0 && energy > 0 ? untilHours(energyRaw, res.energyPerHour, 0) : null,
    deficit: res.energyPerHour < 0 || efficiency < 1,
  };
}

/**
 * Immediate preview of a spend (or refund with negative costs): re-anchors the display to "now" minus the cost.
 * `serverMs` is kept, so the server's own answer (always newer) still replaces it. Never goes below zero.
 */
export function previewSpend(base: ResourceBase, clientNow: number, metal: number, energy: number, rp = 0): ResourceBase {
  const live = projectResources(base, clientNow);
  return {
    ...base,
    anchorMs: clientNow + base.offsetMs,
    res: {
      ...base.res,
      metal: Math.max(0, live.metal - metal),
      energy: Math.max(0, live.energy - energy),
      ...(base.res.rp === undefined ? {} : { rp: Math.max(0, (live.rp ?? 0) - rp) }),
    },
  };
}

// ---- Formatting (Hungarian: decimal comma, space as thousands separator) ----

const NBSP = ' ';

function groupThousands(n: number): string {
  return Math.trunc(Math.abs(n)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

/** Exact value: 12 345 */
export function formatExact(n: number): string {
  return `${n < 0 ? '−' : ''}${groupThousands(n)}`;
}

/** Compact value for the header: 8 000 · 12,3 e · 1,2 M. */
export function formatCompact(n: number): string {
  const a = Math.abs(n);
  const sign = n < 0 ? '−' : '';
  const one = (v: number, unit: string) => {
    const t = Math.floor(v * 10) / 10;
    return `${sign}${t.toFixed(1).replace('.', ',').replace(/,0$/, '')}${NBSP}${unit}`;
  };
  if (a >= 1_000_000) return one(a / 1_000_000, 'M');
  if (a >= 10_000) return one(a / 1000, 'e');
  return formatExact(n);
}

/** Net rate per hour with an explicit sign: +225/h · −40/h · 0/h. */
export function formatRate(perHour: number): string {
  if (perHour === 0) return `0/h`;
  if (Math.abs(perHour) < 10 && !Number.isInteger(perHour)) return `${perHour > 0 ? '+' : '−'}${Math.abs(perHour).toFixed(1).replace('.', ',')}/h`;
  return `${perHour > 0 ? '+' : '−'}${formatCompact(Math.abs(perHour))}/h`;
}

/** "3 ó 20 p" style duration for an ETA in hours. */
export function formatEta(hours: number | null): string {
  if (hours === null) return '–';
  const totalMin = Math.max(1, Math.round(hours * 60));
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d > 0) return `${d} n ${h} ó`;
  if (h > 0) return `${h} ó ${m} p`;
  return `${m} p`;
}

/** Per-minute rate for the economy cards: "+3,8" (kept for the detailed economy view). */
export function perMinute(perHour: number): string {
  const v = perHour / 60;
  const s = Math.abs(v) >= 10 ? Math.round(v).toString() : v.toFixed(1).replace(/\.0$/, '');
  return `${v > 0 ? '+' : ''}${s}`;
}
